/**
 * Member Statistics Cache Utility
 * Stores member statistics with 1 hour expiration
 * Cache is cleared on logout or after 1 hour of inactivity
 */

const CACHE_KEY_PREFIX = 'member_statistics_';
const CACHE_EXPIRY_MS = 60 * 60 * 1000; // 1 hour

export interface MemberStatistics {
  total: number;
  vip: number;
  newThisMonth: number;
  dormant: number;
  timestamp: number;
}

/**
 * Get cache key for member type
 */
const getCacheKey = (isBusiness: boolean): string => {
  return `${CACHE_KEY_PREFIX}${isBusiness ? 'business' : 'general'}`;
};

/**
 * Check if cache is expired
 */
const isCacheExpired = (timestamp: number): boolean => {
  const now = Date.now();
  return now - timestamp > CACHE_EXPIRY_MS;
};

/**
 * Get cached statistics
 */
export const getCachedStatistics = (isBusiness: boolean): MemberStatistics | null => {
  try {
    const cacheKey = getCacheKey(isBusiness);
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) {
      return null;
    }
    
    const statistics: MemberStatistics = JSON.parse(cached);
    
    // Check if cache is expired
    if (isCacheExpired(statistics.timestamp)) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return statistics;
  } catch (error) {
    // console.error('[MemberStatisticsCache] ❌ Error reading cache:', error);
    return null;
  }
};

/**
 * Save statistics to cache
 */
export const saveStatisticsToCache = (
  isBusiness: boolean,
  statistics: Omit<MemberStatistics, 'timestamp'>
): void => {
  try {
    const cacheKey = getCacheKey(isBusiness);
    const data: MemberStatistics = {
      ...statistics,
      timestamp: Date.now(),
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    // console.error('[MemberStatisticsCache] ❌ Error saving cache:', error);
  }
};

/**
 * Clear all member statistics cache
 */
export const clearStatisticsCache = (): void => {
  try {
    localStorage.removeItem(getCacheKey(false)); // general
    localStorage.removeItem(getCacheKey(true)); // business
  } catch (error) {
    // console.error('[MemberStatisticsCache] ❌ Error clearing cache:', error);
  }
};

/**
 * Clear expired cache entries
 */
export const clearExpiredCache = (): void => {
  try {
    const generalCache = localStorage.getItem(getCacheKey(false));
    const businessCache = localStorage.getItem(getCacheKey(true));
    
    if (generalCache) {
      const stats: MemberStatistics = JSON.parse(generalCache);
      if (isCacheExpired(stats.timestamp)) {
        localStorage.removeItem(getCacheKey(false));
      }
    }
    
    if (businessCache) {
      const stats: MemberStatistics = JSON.parse(businessCache);
      if (isCacheExpired(stats.timestamp)) {
        localStorage.removeItem(getCacheKey(true));
      }
    }
  } catch (error) {
    // console.error('[MemberStatisticsCache] ❌ Error clearing expired cache:', error);
  }
};







